// A logged cardio activity: what a run, ride, swim or hike was, in plain numbers. It travels inside the session
// log (SessionLog.activity, stored in the private sessions.body), so the history, XP and Progress read it.
// Privacy (decision log 2026-10-14): by default only the summary is kept, never GPS points; the route points
// are kept only when the person opts in, downsampled, and they stay in the person's own private history.
import type { CardioSport, HeartRateZone } from '../types';
import { CARDIO_SPORTS, isCardioSport, sportVariant } from './sports';
import { haversineM } from './units';

/**
 * One route point, in a shape a map can draw later (GeoJSON order is lon, lat; we keep lat first like GPX):
 * latitude, longitude (degrees, 5 decimals, about 1 m), elevation in m, seconds from the start, heart rate.
 */
export type RoutePoint = [lat: number, lon: number, ele: number | null, t: number | null, hr: number | null];

/** One km split: the km number (the last one may be shorter), its distance in m, time in s, climb in m, average heart rate. */
export interface Split { km: number; distanceM: number; sec: number; gainM: number; avgHr?: number }

export interface CardioActivity {
  v: 1;
  sport: CardioSport;
  /** One of the sport's variants (CARDIO_SPORTS[sport].variants). */
  variant?: string;
  indoor?: boolean;
  /** Moving time in seconds (the time that counts for pace and XP). */
  durationSec: number;
  /** Start to finish, pauses included, when known. */
  elapsedSec?: number;
  distanceM?: number;
  elevationGainM?: number;
  elevationLossM?: number;
  avgHr?: number;
  maxHr?: number;
  /** Seconds in heart rate zones 1 to 5. */
  zoneSec?: [number, number, number, number, number];
  /** How hard it felt, 1 (very easy) to 10 (all out). */
  rpe?: number;
  /** Steps per minute (running) or turns per minute (cycling, rowing). */
  avgCadence?: number;
  /** Swimming. */
  poolLengthM?: number;
  stroke?: string;
  /** Mountain biking: S0 to S5 or green, blue, red, black (TRAIL_DIFFICULTY). */
  trailDifficulty?: string;
  /** Where the numbers come from. */
  source: 'manual' | 'gpx' | 'tcx' | 'fit' | 'strava' | 'player' | 'chat';
  /** What the import found, without any position: how many points, and whether it had heart rate and elevation. */
  track?: { points: number; hasHr: boolean; hasElevation: boolean };
  /** Splits per km (from a recorded track), without positions. */
  splits?: Split[];
  /** Elevation profile without positions: [distance from the start in m, elevation in m], at most 200 points. */
  profile?: Array<[number, number]>;
  /** Only when the person chose "Keep the route": downsampled points, private to the person. */
  route?: RoutePoint[];
  notes?: string;
  /** The Strava activity id (from activities.csv, a shared or pasted link, or the coach's import); "Open in Strava". */
  stravaId?: string;
  /** A Strava short link (https://strava.app.link/...) as it was shared, when the id is not known. Never resolved. */
  stravaUrl?: string;
}

/** Limits, so one activity cannot fill the history (the whole session body is limited to 256 KB). */
export const ACTIVITY_LIMITS = {
  maxDurationSec: 48 * 3600,
  maxDistanceM: 1_000_000,
  maxElevationM: 20_000,
  minHr: 30,
  maxHr: 250,
  maxRoutePoints: 2_000,
  maxSplits: 300,
  maxProfilePoints: 200,
  maxNotes: 1_000,
  /** Bytes of the activity as JSON. */
  maxBytes: 160_000,
} as const;

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const optRange = (v: unknown, lo: number, hi: number) => v === undefined || (num(v) && v >= lo && v <= hi);

/** The first problem of an activity in plain words, or null when it is fine to save. */
export function activityProblem(a: unknown): string | null {
  if (!isObj(a)) return 'the activity is not an object';
  if (a.v !== 1) return 'unknown activity version';
  if (!isCardioSport(a.sport)) return 'unknown sport';
  const L = ACTIVITY_LIMITS;
  if (a.variant !== undefined && (typeof a.variant !== 'string' || !Object.hasOwn(CARDIO_SPORTS[a.sport].variants, a.variant))) return 'unknown variant for this sport';
  if (!num(a.durationSec) || a.durationSec <= 0 || a.durationSec > L.maxDurationSec) return 'the duration must be between 1 second and 48 hours';
  if (!optRange(a.elapsedSec, 0, L.maxDurationSec * 2)) return 'the elapsed time is out of range';
  if (!optRange(a.distanceM, 0, L.maxDistanceM)) return 'the distance must be between 0 and 1\'000 km';
  if (!optRange(a.elevationGainM, 0, L.maxElevationM) || !optRange(a.elevationLossM, 0, L.maxElevationM)) return 'the elevation must be between 0 and 20\'000 m';
  if (!optRange(a.avgHr, L.minHr, L.maxHr) || !optRange(a.maxHr, L.minHr, L.maxHr)) return 'the heart rate must be between 30 and 250 bpm';
  if (num(a.avgHr) && num(a.maxHr) && a.avgHr > a.maxHr) return 'the average heart rate is above the maximum';
  if (!optRange(a.rpe, 1, 10)) return 'the effort must be between 1 and 10';
  if (!optRange(a.avgCadence, 0, 400)) return 'the cadence is out of range';
  if (!optRange(a.poolLengthM, 10, 100)) return 'the pool length must be between 10 and 100 m';
  if (a.zoneSec !== undefined) {
    if (!Array.isArray(a.zoneSec) || a.zoneSec.length !== 5 || !a.zoneSec.every((z) => num(z) && z >= 0)) return 'the zone times must be 5 numbers';
    const total = (a.zoneSec as number[]).reduce((s, z) => s + z, 0);
    if (total > (num(a.elapsedSec) ? Math.max(a.elapsedSec, a.durationSec) : a.durationSec) * 1.05 + 60) return 'the zone times are longer than the activity';
  }
  for (const k of ['stroke', 'trailDifficulty'] as const) if (a[k] !== undefined && (typeof a[k] !== 'string' || (a[k] as string).length > 40)) return `${k} is not a short text`;
  if (!['manual', 'gpx', 'tcx', 'fit', 'strava', 'player', 'chat'].includes(a.source as string)) return 'unknown source';
  if (a.notes !== undefined && (typeof a.notes !== 'string' || a.notes.length > L.maxNotes)) return 'the notes are longer than 1\'000 characters';
  if (a.stravaId !== undefined && (typeof a.stravaId !== 'string' || !/^[1-9]\d{0,19}$/.test(a.stravaId))) return 'the Strava id is not a Strava activity number';
  if (a.stravaUrl !== undefined && (typeof a.stravaUrl !== 'string' || !/^https:\/\/strava\.app\.link\/[A-Za-z0-9_-]{4,40}$/.test(a.stravaUrl))) return 'the Strava link is not a Strava short link';
  if (a.track !== undefined && (!isObj(a.track) || !num(a.track.points))) return 'the track summary is not valid';
  if (a.route !== undefined) {
    if (!Array.isArray(a.route) || a.route.length > L.maxRoutePoints) return `the route has more than ${L.maxRoutePoints} points`;
    for (const p of a.route) {
      if (!Array.isArray(p) || p.length !== 5 || !num(p[0]) || !num(p[1]) || Math.abs(p[0]) > 90 || Math.abs(p[1]) > 180) return 'a route point is not valid';
      if ([p[2], p[3], p[4]].some((v) => v !== null && !num(v))) return 'a route point is not valid';
    }
  }
  if (a.splits !== undefined) {
    if (!Array.isArray(a.splits) || a.splits.length > L.maxSplits) return 'too many splits';
    if (!a.splits.every((x) => isObj(x) && num(x.km) && num(x.distanceM) && num(x.sec) && num(x.gainM))) return 'a split is not valid';
  }
  if (a.profile !== undefined) {
    if (!Array.isArray(a.profile) || a.profile.length > L.maxProfilePoints) return 'the elevation profile has too many points';
    if (!a.profile.every((p) => Array.isArray(p) && p.length === 2 && num(p[0]) && num(p[1]))) return 'the elevation profile is not valid';
  }
  if (JSON.stringify(a).length > L.maxBytes) return 'the activity is too large';
  return null;
}

// Heart rate zones --------------------------------------------------------------------------------

/** Zone floors as a share of the maximum heart rate: Z1 from 50 %, Z2 60 %, Z3 70 %, Z4 80 %, Z5 90 %. */
export const ZONE_FLOORS = [0.5, 0.6, 0.7, 0.8, 0.9] as const;

/** Used when the person has not set a maximum heart rate. */
export const DEFAULT_MAX_HR = 190;

export const ZONE_LABELS: Record<HeartRateZone, string> = {
  1: 'Z1 · very easy', 2: 'Z2 · easy, can talk', 3: 'Z3 · moderate', 4: 'Z4 · hard', 5: 'Z5 · all out',
};

/** The zone of a heart rate (below 50 % counts as zone 1). */
export function zoneOf(hr: number, maxHr = DEFAULT_MAX_HR): HeartRateZone {
  const share = hr / maxHr;
  let z = 1;
  for (let i = 0; i < ZONE_FLOORS.length; i++) if (share >= ZONE_FLOORS[i]!) z = i + 1;
  return z as HeartRateZone;
}

/** The heart rate range of a zone in bpm, for the plain words ("Zone 2, 114 to 133 bpm"). */
export function zoneRange(zone: HeartRateZone, maxHr = DEFAULT_MAX_HR): [number, number] {
  const lo = Math.round(ZONE_FLOORS[zone - 1]! * maxHr);
  const hi = zone === 5 ? maxHr : Math.round(ZONE_FLOORS[zone]! * maxHr) - 1;
  return [lo, hi];
}

// From a track (GPX, FIT) to a summary --------------------------------------------------------------

/** One sample of a recorded track. Missing values are undefined. */
export interface TrackSample {
  /** Milliseconds since 1970. */
  time?: number;
  lat?: number;
  lon?: number;
  ele?: number;
  hr?: number;
  cadence?: number;
  /** Distance from the start in m, when the device recorded it (FIT). */
  distanceM?: number;
}

/** What a parsed file gives: the samples and what the file says about itself. */
export interface ParsedTrack {
  format: 'gpx' | 'tcx' | 'fit';
  name?: string;
  /** The sport word in the file ("running", "cycling", "biking"), mapped by sportFromFile(). */
  sportHint?: string;
  subSportHint?: string;
  samples: TrackSample[];
  /** Totals the device computed itself (FIT session message); they win over our own sums. */
  totals?: { durationSec?: number; elapsedSec?: number; distanceM?: number; elevationGainM?: number; elevationLossM?: number; avgHr?: number; maxHr?: number; avgCadence?: number; poolLengthM?: number };
  startedAt?: number;
}

/** A pause longer than this between two samples is not moving time. */
const PAUSE_MS = 30_000;
/** Elevation changes smaller than this are GPS noise (a simple hysteresis, like most watches). */
const ELE_THRESHOLD_M = 3;
/**
 * Standing still: over the last STILL_WINDOW_S seconds the position moved less than STILL_SPEED m/s in a straight
 * line, so the GPS drift of a stop at a traffic light adds neither distance nor moving time.
 */
const STILL_WINDOW_S = 20;
const STILL_SPEED = 0.3;

/**
 * Per segment (from sample i - 1 to sample i): its distance in m (0 for a pause or a stop) and its moving
 * seconds (0 for a pause over 30 s, a stop, or missing times). Index 0 is always 0. One pass, linear.
 */
/**
 * Segments (by their end index) where the person stood still: the straight-line distance over a window of at
 * least 20 s before or after is under 0.3 m/s. Looking both ways catches the whole stop, also its first seconds.
 * Two linear passes with a moving window start.
 */
function standstill(samples: TrackSample[]): Uint8Array {
  const n = samples.length;
  const out = new Uint8Array(n);
  const pass = (forward: boolean) => {
    const at = (k: number) => samples[forward ? k : n - 1 - k]!;
    let j = 0;
    for (let k = 1; k < n; k++) {
      const b = at(k);
      if (b.time === undefined || b.lat === undefined || b.lon === undefined) { j = k; continue; }
      const prev = at(k - 1);
      if (prev.time === undefined || Math.abs(b.time - prev.time) > PAUSE_MS) { j = k; continue; }
      while (j < k - 1) {
        const t1 = at(j + 1).time;
        if (t1 === undefined || Math.abs(b.time - t1) < STILL_WINDOW_S * 1000) break;
        j++;
      }
      const w = at(j);
      const span = w.time !== undefined ? Math.abs(b.time - w.time) / 1000 : 0;
      if (span >= STILL_WINDOW_S && w.lat !== undefined && w.lon !== undefined && haversineM(w.lat, w.lon, b.lat, b.lon) / span < STILL_SPEED) {
        // Backward: the segment ending at k. Forward (reversed order): the segment ending at the original index.
        out[forward ? k : n - k] = 1;
      }
    }
  };
  pass(true);
  pass(false);
  return out;
}

export function trackSegments(samples: TrackSample[]): { dist: Float64Array; sec: Float64Array; device: boolean } {
  const n = samples.length;
  const dist = new Float64Array(n);
  const sec = new Float64Array(n);
  let device = false;
  for (let i = 0; i < n; i++) if (samples[i]!.distanceM !== undefined) { device = true; break; }
  const still = device ? null : standstill(samples);
  let lastDevice: number | undefined;
  for (let i = 0; i < n; i++) {
    const b = samples[i]!;
    if (device && b.distanceM !== undefined) {
      if (lastDevice !== undefined && b.distanceM >= lastDevice) dist[i] = b.distanceM - lastDevice;
      lastDevice = b.distanceM;
    }
    if (i === 0) continue;
    const a = samples[i - 1]!;
    const dt = a.time !== undefined && b.time !== undefined ? (b.time - a.time) / 1000 : 0;
    const pause = dt * 1000 > PAUSE_MS;
    const stop = still?.[i] === 1;
    const hasPos = a.lat !== undefined && a.lon !== undefined && b.lat !== undefined && b.lon !== undefined;
    if (!device) dist[i] = hasPos && !pause && !stop ? haversineM(a.lat!, a.lon!, b.lat!, b.lon!) : 0;
    else if (pause) dist[i] = 0;
    sec[i] = dt > 0 && !pause && !stop ? dt : 0;
  }
  return { dist, sec, device };
}

export interface TrackSummary {
  startedAt: number | null;
  durationSec: number;
  elapsedSec: number;
  distanceM: number;
  elevationGainM: number;
  elevationLossM: number;
  avgHr?: number;
  maxHr?: number;
  zoneSec?: [number, number, number, number, number];
  avgCadence?: number;
  track: { points: number; hasHr: boolean; hasElevation: boolean };
}

/**
 * Sums up a track: moving time (pauses over 30 s and standing still left out), distance (the device's own when
 * it recorded one, else from the points while moving), elevation gain and loss with a 3 m threshold, heart rate
 * average, maximum and time in zones, average cadence. The device's own totals (FIT, TCX laps) win where they
 * exist. Linear, no spread over large arrays (200'000 points work).
 */
export function summarizeTrack(track: ParsedTrack, maxHr = DEFAULT_MAX_HR): TrackSummary {
  const s = track.samples;
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const x of s) if (x.time !== undefined && Number.isFinite(x.time)) { if (x.time < tMin) tMin = x.time; if (x.time > tMax) tMax = x.time; }
  const hasTimes = tMin <= tMax;
  const startedAt = track.startedAt ?? (hasTimes ? tMin : null);
  const elapsedSec = hasTimes ? (tMax - tMin) / 1000 : 0;

  const seg = trackSegments(s);
  let distance = 0;
  let moving = 0;
  const zones: [number, number, number, number, number] = [0, 0, 0, 0, 0];
  let hrWeighted = 0;
  let hrTime = 0;
  let maxHrSeen = 0;
  let cadWeighted = 0;
  let cadTime = 0;
  let hasEle = false;
  for (let i = 0; i < s.length; i++) {
    const x = s[i]!;
    if (x.hr !== undefined && x.hr > maxHrSeen) maxHrSeen = x.hr;
    if (x.ele !== undefined) hasEle = true;
    distance += seg.dist[i]!;
    const dt = seg.sec[i]!;
    if (i === 0 || dt <= 0) continue;
    const a = s[i - 1]!;
    moving += dt;
    if (a.hr !== undefined && a.hr > 0) {
      hrWeighted += a.hr * dt;
      hrTime += dt;
      zones[zoneOf(a.hr, maxHr) - 1]! += dt;
    }
    if (a.cadence !== undefined && a.cadence > 0) {
      cadWeighted += a.cadence * dt;
      cadTime += dt;
    }
  }

  // Elevation with hysteresis: only a change of 3 m or more from the last turning point counts.
  const climb = new Hysteresis();
  for (const x of s) if (x.ele !== undefined) climb.add(x.ele);

  const t = track.totals ?? {};
  const hasHr = hrTime > 0 || maxHrSeen > 0;
  const round = (n: number) => Math.round(n);
  const avgHr = t.avgHr ?? (hrTime > 0 ? hrWeighted / hrTime : undefined);
  const topHr = t.maxHr ?? (maxHrSeen > 0 ? maxHrSeen : undefined);
  const avgCadence = t.avgCadence ?? (cadTime > 0 ? cadWeighted / cadTime : undefined);
  return {
    startedAt,
    durationSec: round(t.durationSec ?? (moving || elapsedSec)),
    elapsedSec: round(t.elapsedSec ?? elapsedSec),
    distanceM: round(t.distanceM ?? distance),
    elevationGainM: round(t.elevationGainM ?? climb.gain),
    elevationLossM: round(t.elevationLossM ?? climb.loss),
    ...(avgHr !== undefined ? { avgHr: round(avgHr) } : {}),
    ...(topHr !== undefined ? { maxHr: round(topHr) } : {}),
    ...(hrTime > 0 ? { zoneSec: zones.map(round) as TrackSummary['zoneSec'] } : {}),
    ...(avgCadence !== undefined ? { avgCadence: round(avgCadence) } : {}),
    track: { points: s.length, hasHr, hasElevation: hasEle },
  };
}

/** Elevation gain and loss with the 3 m threshold; `take()` reads the gain since the last call (for splits). */
class Hysteresis {
  gain = 0;
  loss = 0;
  private ref: number | undefined;
  private taken = 0;
  add(ele: number): void {
    if (this.ref === undefined) { this.ref = ele; return; }
    const d = ele - this.ref;
    if (d >= ELE_THRESHOLD_M) { this.gain += d; this.ref = ele; } else if (d <= -ELE_THRESHOLD_M) { this.loss -= d; this.ref = ele; }
  }
  take(): number {
    const g = this.gain - this.taken;
    this.taken = this.gain;
    return g;
  }
}

/**
 * Route points for "Keep the route": every n-th sample so that at most maxPoints remain (first and last
 * always kept), positions rounded to 5 decimals (about 1 m), time in seconds from the start.
 */
export function downsampleRoute(samples: TrackSample[], maxPoints: number = ACTIVITY_LIMITS.maxRoutePoints): RoutePoint[] {
  const pts = samples.filter((x) => x.lat !== undefined && x.lon !== undefined);
  if (pts.length === 0) return [];
  const start = pts.find((x) => x.time !== undefined)?.time;
  const step = Math.max(1, Math.ceil(pts.length / maxPoints));
  const picked = pts.filter((_, i) => i % step === 0);
  if (picked.at(-1) !== pts.at(-1)) picked.push(pts.at(-1)!);
  const r5 = (n: number) => Math.round(n * 1e5) / 1e5;
  return picked.slice(0, maxPoints).map((x) => [
    r5(x.lat!), r5(x.lon!), x.ele === undefined ? null : Math.round(x.ele * 10) / 10,
    x.time !== undefined && start !== undefined ? Math.round((x.time - start) / 1000) : null,
    x.hr ?? null,
  ]);
}

/** Distance from the start at each sample, only while moving (pauses and stops add nothing). */
function cumulativeDistance(samples: TrackSample[]): Float64Array {
  const { dist } = trackSegments(samples);
  const out = new Float64Array(samples.length);
  let d = 0;
  for (let i = 0; i < samples.length; i++) { d += dist[i]!; out[i] = d; }
  return out;
}

/**
 * Splits per km from a track with times: moving time, climb (with the same 3 m threshold as the total) and
 * average heart rate of each full km, plus the last part when it is at least 100 m.
 */
export function trackSplits(samples: TrackSample[]): Split[] {
  const { dist, sec: segSec } = trackSegments(samples);
  const splits: Split[] = [];
  const climb = new Hysteresis();
  let km = 1;
  let sec = 0;
  let hrSum = 0;
  let hrSec = 0;
  let startD = 0;
  let d = 0;
  const flush = (endD: number) => {
    splits.push({ km, distanceM: Math.round(endD - startD), sec: Math.round(sec), gainM: Math.round(climb.take()), ...(hrSec > 0 ? { avgHr: Math.round(hrSum / hrSec) } : {}) });
    km++; sec = 0; hrSum = 0; hrSec = 0; startD = endD;
  };
  if (samples[0]?.ele !== undefined) climb.add(samples[0].ele);
  for (let i = 1; i < samples.length && splits.length < ACTIVITY_LIMITS.maxSplits; i++) {
    const a = samples[i - 1]!;
    const b = samples[i]!;
    const dt = segSec[i]!;
    if (dt > 0) {
      sec += dt;
      if (a.hr !== undefined) { hrSum += a.hr * dt; hrSec += dt; }
    }
    if (b.ele !== undefined) climb.add(b.ele);
    d += dist[i]!;
    while (d - startD >= 1000 && splits.length < ACTIVITY_LIMITS.maxSplits) flush(startD + 1000);
  }
  const rest = d - startD;
  if (rest >= 100 && splits.length < ACTIVITY_LIMITS.maxSplits) flush(startD + rest);
  return splits.length > 0 && splits.every((s) => s.sec > 0) ? splits : [];
}

/** Elevation profile without positions: at most `maxPoints` pairs of distance (m) and elevation (m). */
export function elevationProfile(samples: TrackSample[], maxPoints: number = ACTIVITY_LIMITS.maxProfilePoints): Array<[number, number]> {
  const dist = cumulativeDistance(samples);
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < samples.length; i++) { const e = samples[i]!.ele; if (e !== undefined) pts.push([Math.round(dist[i]!), e]); }
  if (pts.length < 2) return [];
  const step = Math.max(1, Math.ceil(pts.length / maxPoints));
  const out = pts.filter((_, i) => i % step === 0).map(([d, e]) => [d, Math.round(e)] as [number, number]);
  const last = pts.at(-1)!;
  if (out.at(-1)![0] !== last[0] && out.length < maxPoints) out.push([last[0], Math.round(last[1])]);
  return out.slice(0, maxPoints);
}

/** The sport and variant a file names ("running", "trail_running", "cycling", "mountain", "lap_swimming"). */
export function sportFromFile(hint?: string, subHint?: string): { sport: CardioSport; variant: string } | null {
  const h = (hint ?? '').toLowerCase().replace(/[\s-]+/g, '_');
  const sub = (subHint ?? '').toLowerCase().replace(/[\s-]+/g, '_');
  const pick = (sport: CardioSport, variant?: string) => ({ sport, variant: sportVariant(sport, variant) });
  if (/mountain_?bik|mtb/.test(h) || (/cycl|bik|ride/.test(h) && /mountain|mtb|downhill/.test(sub))) return pick('mountain_biking');
  if (/trail_run/.test(h) || (/run/.test(h) && /trail/.test(sub))) return pick('running', 'trail');
  if (/treadmill/.test(h) || (/run/.test(h) && /treadmill|indoor/.test(sub))) return pick('running', 'treadmill');
  if (/track/.test(sub) && /run/.test(h)) return pick('running', 'track');
  if (/run|jog/.test(h)) return pick('running', 'road');
  if (/gravel/.test(h) || /gravel/.test(sub)) return pick('cycling', 'gravel');
  if ((/cycl|bik|ride/.test(h) && /indoor|virtual|trainer|spin/.test(sub)) || /virtual_ride|indoor_cycl/.test(h)) return pick('cycling', 'indoor');
  if (/cycl|bik|ride|road/.test(h)) return pick('cycling', 'road');
  if (/swim/.test(h)) return pick('swimming', /open/.test(sub) || /open/.test(h) ? 'open_water' : 'pool');
  if (/row/.test(h)) return pick('rowing', /indoor|machine/.test(sub) ? 'machine' : 'water');
  if (/hik|mountaineer/.test(h)) return pick('hiking');
  if (/walk/.test(h)) return pick('walking');
  if (/touring|backcountry/.test(h) || /touring|backcountry/.test(sub)) return pick('skiing', 'touring');
  if (/skate_ski|skating_ski/.test(h) || /skate/.test(sub)) return pick('skiing', 'skate');
  if (/cross_country|nordic|xc_ski|ski/.test(h)) return pick('skiing', 'classic');
  if (/inline|roller/.test(h)) return pick('inline_skating');
  if (/ellip/.test(h)) return pick('elliptical');
  if (/stair|step/.test(h)) return pick('stair_climbing');
  return null;
}
